/**
 * ScheduledReminders.jsx
 *
 * List + CRUD panel for scheduled reminders inside NotificationCenter.
 *
 * Delete flow (two steps matching existing patterns):
 *   1. deleteRule(rule_id)                   — soft-delete notification_rules
 *   2. deleteCalendarEvent(calendar_event_id) — hard-delete calendar_events row
 *
 * Active toggle: updateRule(rule_id, { is_active }) — reuses existing function.
 *
 * Props:
 *   showSnack — (severity, message) => void
 */

import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Skeleton,
  Tooltip,
  Switch,
  Button
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import ScheduleIcon from '@mui/icons-material/Schedule'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useTranslation } from 'react-i18next'
import DeleteDialog from '../../DeleteDialog'
import ReminderEditorDialog from './ReminderEditorDialog'
import {
  getReminders,
  deleteRule,
  deleteCalendarEvent,
  updateRule
} from '../../../services/notificationRules'

// ─── Helpers ──────────────────────────────────────────────────────────────────
const brandRadius = () => (window.$brand === 'PRB' ? 0 : 2)

function rruleSummary(rrule, startsAt) {
  // Fixed date — no rrule, just a date
  if (!rrule && startsAt) {
    return new Date(startsAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  }
  if (!rrule) return '—'
  const m = rrule.match(/FREQ=([A-Z]+)/)
  if (!m) return rrule
  const freq = m[1]
  if (!startsAt) {
    return { DAILY: 'Daily', WEEKLY: 'Weekly', MONTHLY: 'Monthly', YEARLY: 'Annually' }[freq] || freq
  }
  const d = new Date(startsAt)
  const dayName = d.toLocaleDateString('en-US', { weekday: 'long' })
  const dayOfMonth = d.getDate()
  const monthName = d.toLocaleDateString('en-US', { month: 'long' })
  switch (freq) {
    case 'DAILY':   return 'Daily'
    case 'WEEKLY':  return `Weekly on ${dayName}`
    case 'MONTHLY': return `Monthly on the ${dayOfMonth}`
    case 'YEARLY':  return `Annually on ${monthName} ${dayOfMonth}`
    default:        return freq
  }
}

// ─── LocaleChip ───────────────────────────────────────────────────────────────
const LocaleChip = ({ locale, filled }) =>
  filled ? (
    <Chip label={locale.toUpperCase()} size='small' color='primary'
      sx={{ height: 18, fontSize: 10, fontWeight: 700, mr: 0.5 }} />
  ) : (
    <Chip label={locale.toUpperCase()} size='small' variant='outlined'
      sx={{ height: 18, fontSize: 10, fontWeight: 700, mr: 0.5, opacity: 0.35 }} />
  )

// ─── SkeletonRows ─────────────────────────────────────────────────────────────
const SkeletonRows = () =>
  Array.from({ length: 3 }).map((_, i) => (
    <Box key={i} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5,
        borderBottom: '1px solid', borderColor: 'divider', gap: 1.5 }}>
      <Skeleton variant='circular' width={20} height={20} />
      <Box sx={{ flexGrow: 1 }}>
        <Skeleton variant='text' width='50%' height={18} />
        <Skeleton variant='text' width='35%' height={14} />
      </Box>
      <Skeleton variant='rectangular' width={40} height={20} sx={{ borderRadius: 1, mr: 1 }} />
      <Skeleton variant='rectangular' width={56} height={28} sx={{ borderRadius: 1 }} />
    </Box>
  ))

// ─── ReminderRow ──────────────────────────────────────────────────────────────
const ReminderRow = ({ reminder, onPreview, onEdit, onDelete, onToggleActive }) => {
  const { t } = useTranslation()
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', px: 2, py: 1.25,
        borderBottom: '1px solid', borderColor: 'divider',
        transition: 'background 0.1s', '&:hover': { bgcolor: 'action.hover' }, cursor: 'pointer'
      }}
      onClick={() => onPreview(reminder)}
    >
      <Box sx={{ color: 'text.secondary', display: 'flex', mr: 1.5, flexShrink: 0 }}>
        <ScheduleIcon fontSize='small' />
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant='body2' fontWeight={500} noWrap>
          {reminder.description || t('Untitled reminder')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.25, gap: 0.5 }}>
          <LocaleChip locale='en' filled={!!reminder.has_en} />
          <LocaleChip locale='es' filled={!!reminder.has_es} />
          <Typography variant='caption' color='text.disabled'>
            {rruleSummary(reminder.rrule, reminder.starts_at)}
          </Typography>
        </Box>
      </Box>

      <Tooltip title={reminder.is_active ? t('Active — click to pause') : t('Paused — click to activate')}>
        <Switch
          size='small'
          checked={!!reminder.is_active}
          onClick={(e) => { e.stopPropagation(); onToggleActive(reminder) }}
          sx={{ mr: 0.5 }}
        />
      </Tooltip>

      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        <Tooltip title={t('Edit')}>
          <IconButton size='small' onClick={(e) => { e.stopPropagation(); onEdit(reminder) }}>
            <EditOutlinedIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('Delete')}>
          <IconButton size='small' sx={{ color: '#d32f2f' }}
            onClick={(e) => { e.stopPropagation(); onDelete(reminder) }}>
            <DeleteOutlinedIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>

      <ChevronRightIcon sx={{ fontSize: 18, color: 'text.disabled', ml: 0.5, flexShrink: 0 }} />
    </Box>
  )
}

// ─── SectionHeader ────────────────────────────────────────────────────────────
const SectionHeader = ({ label, buttonLabel, onAdd }) => (
  <Box sx={{ px: 2, pt: 2, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <Typography variant='caption' color='text.secondary' fontWeight={600}
      sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Button variant='outlined' size='small' startIcon={<AddIcon />}
      sx={{ borderRadius: brandRadius(), py: 0.25, fontSize: 11 }}
      onClick={onAdd}>
      {buttonLabel}
    </Button>
  </Box>
)

// ─── ScheduledReminders ───────────────────────────────────────────────────────
export default function ScheduledReminders({ showSnack }) {
  const { t } = useTranslation()

  const [reminders, setReminders] = useState([])
  const [loading, setLoading] = useState(true)

  const [editorOpen, setEditorOpen] = useState(false)
  const [editorReminder, setEditorReminder] = useState(null)
  const [editorReadOnly, setEditorReadOnly] = useState(false)

  const [deleteTarget, setDeleteTarget] = useState(null)

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const loadReminders = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getReminders()
      setReminders(Array.isArray(data) ? data : [])
    } catch {
      showSnack?.('error', t('Failed to load reminders'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => { loadReminders() }, [loadReminders])

  // ── Open modes ─────────────────────────────────────────────────────────────
  const openCreate  = ()         => { setEditorReminder(null);    setEditorReadOnly(false); setEditorOpen(true) }
  const openEdit    = (reminder) => { setEditorReminder(reminder); setEditorReadOnly(false); setEditorOpen(true) }
  const openPreview = (reminder) => { setEditorReminder(reminder); setEditorReadOnly(true);  setEditorOpen(true) }

  const handleSaved = () => {
    loadReminders()
    showSnack?.('success', editorReminder ? t('Reminder updated') : t('Reminder created'))
  }

  // ── Active toggle — reuses existing updateRule ─────────────────────────────
  const handleToggleActive = async (reminder) => {
    // Optimistic
    setReminders((prev) =>
      prev.map((r) => r.rule_id === reminder.rule_id ? { ...r, is_active: !r.is_active } : r)
    )
    try {
      await updateRule(reminder.rule_id, { is_active: !reminder.is_active })
    } catch {
      // Revert
      setReminders((prev) =>
        prev.map((r) => r.rule_id === reminder.rule_id ? { ...r, is_active: reminder.is_active } : r)
      )
      showSnack?.('error', t('Failed to update reminder'))
    }
  }

  // ── Delete — soft-delete rule, then hard-delete calendar_event ─────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    try {
      await deleteRule(deleteTarget.rule_id)
      if (deleteTarget.calendar_event_id) {
        await deleteCalendarEvent(deleteTarget.calendar_event_id)
      }
      setReminders((prev) => prev.filter((r) => r.rule_id !== deleteTarget.rule_id))
      showSnack?.('success', t('Reminder deleted'))
    } catch {
      showSnack?.('error', t('Failed to delete reminder'))
    } finally {
      setDeleteTarget(null)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <SectionHeader
        label={t('Scheduled reminders')}
        buttonLabel={t('New reminder')}
        onAdd={openCreate}
      />

      {loading ? (
        <SkeletonRows />
      ) : reminders.length > 0 ? (
        reminders.map((r) => (
          <ReminderRow
            key={r.rule_id}
            reminder={r}
            onPreview={openPreview}
            onEdit={openEdit}
            onDelete={setDeleteTarget}
            onToggleActive={handleToggleActive}
          />
        ))
      ) : (
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant='body2' color='text.disabled'>
            {t('No scheduled reminders yet.')}
          </Typography>
        </Box>
      )}

      <ReminderEditorDialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        onSaved={handleSaved}
        reminder={editorReminder}
        readOnly={editorReadOnly}
      />

      <DeleteDialog
        item={deleteTarget ?? {}}
        title={t('delete_reminder_title')}
        caption={t('delete_reminder_caption')}
        targetFunc={handleDeleteConfirm}
        handleDialogClose={() => setDeleteTarget(null)}
      />
    </>
  )
}